// FILE: src/components/LedgerPage.jsx
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import API from "../api";

// MUI imports
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  TablePagination,
} from "@mui/material";

function LedgerPage() {
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filters
  const [accountId, setAccountId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Accounts for dropdown
  const [accounts, setAccounts] = useState([]);

  // For pagination
  const [page, setPage] = useState(0); // MUI is zero-based; server is one-based
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Manage URL query parameters
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    const paramAccountId = searchParams.get("accountId") || "";
    const paramStartDate = searchParams.get("startDate") || "";
    const paramEndDate = searchParams.get("endDate") || "";

    setAccountId(paramAccountId);
    setStartDate(paramStartDate);
    setEndDate(paramEndDate);

    // Convert from zero-based page to one-based for server
    const paramPage = parseInt(searchParams.get("page")) || 1;
    const paramLimit = parseInt(searchParams.get("limit")) || rowsPerPage;

    // Update local pagination states
    setPage(paramPage - 1);
    setRowsPerPage(paramLimit);

    // Call fetchLedger with these values
    fetchLedger(paramAccountId, paramStartDate, paramEndDate, paramPage, paramLimit);
    // eslint-disable-next-line
  }, [searchParams]);

  // Fetch all accounts for the filter dropdown
  const fetchAccounts = async () => {
    try {
      const res = await API.get("/accounts?limit=100000000");
      setAccounts(res.data.accounts || []);
    } catch (err) {
      console.error("Error loading accounts:", err);
      alert("Failed to load accounts. Please try again.");
    }
  };

  // Fetch ledger entries with optional filters + pagination
  const fetchLedger = async (
    filterAccountId = "",
    filterStartDate = "",
    filterEndDate = "",
    pageNumber = 1,
    limitNumber = 10
  ) => {
    setLoading(true);
    try {
      const params = {
        page: pageNumber,
        limit: limitNumber,
      };
      if (filterAccountId) params.accountId = filterAccountId;
      if (filterStartDate) params.startDate = filterStartDate;
      if (filterEndDate) params.endDate = filterEndDate;

      const res = await API.get("/ledgers?limit=100000000", { params });
      // The server returns: {
      //   openingBalance,
      //   entries,
      //   totalCount,
      //   currentPage,
      //   pageSize
      // }

      setOpeningBalance(res.data.openingBalance || 0);
      setLedgerEntries(res.data.entries || []);
      setTotalCount(res.data.totalCount || 0);
    } catch (err) {
      console.error("Error loading ledger:", err);
      alert("Failed to load ledger entries. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle search button
  const handleSearch = () => {
    // We'll reset to page=1 whenever the user changes filters
    const newParams = {
      page: 1,
      limit: rowsPerPage,
    };
    if (accountId) newParams.accountId = accountId;
    if (startDate) newParams.startDate = startDate;
    if (endDate) newParams.endDate = endDate;

    setSearchParams(newParams);
  };

  // Clear all filters
  const handleClearFilter = () => {
    setAccountId("");
    setStartDate("");
    setEndDate("");
    setLedgerEntries([]);
    setOpeningBalance(0);
    setTotalCount(0);
    setPage(0);
    setRowsPerPage(10);
    setSearchParams({});
  };

  // Handle pagination changes
  const handleChangePage = (event, newPage) => {
    // Convert newPage (0-based) to 1-based for server
    const pageNumber = newPage + 1;

    // Keep existing filters in search params
    const newParams = {
      page: pageNumber,
      limit: rowsPerPage,
    };
    if (accountId) newParams.accountId = accountId;
    if (startDate) newParams.startDate = startDate;
    if (endDate) newParams.endDate = endDate;

    setSearchParams(newParams);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    // Reset to first page
    const pageNumber = 1;

    const newParams = {
      page: pageNumber,
      limit: newLimit,
    };
    if (accountId) newParams.accountId = accountId;
    if (startDate) newParams.startDate = startDate;
    if (endDate) newParams.endDate = endDate;

    setSearchParams(newParams);
  };

  // If you want to filter by account on click of Amount/Balance:
  const handleRunningBalanceClick = (accId) => {
    if (accId === accountId) {
      handleClearFilter();
    } else {
      // set filter to this account only
      const newParams = {
        page: 1,
        limit: rowsPerPage,
        accountId: accId,
      };
      if (startDate) newParams.startDate = startDate;
      if (endDate) newParams.endDate = endDate;
      setSearchParams(newParams);
    }
  };

  // Build ascending rows with a special "opening" row at top
  function buildRowsAscending() {
    let runningTotal = openingBalance;
    const rows = [
      {
        _id: "OPENING-ROW",
        isOpeningRow: true,
        dateLabel: "Opening Balance",
        runningBalance: runningTotal,
      },
    ];

    for (const entry of ledgerEntries) {
      const net = entry.debit - entry.credit;
      runningTotal += net;

      rows.push({
        _id: entry._id,
        date: new Date(entry.date),
        account: entry.account,
        description: entry.description,
        transactionType: entry.transaction?.type,
        categoryName: entry.transaction?.category?.name,
        amount: net,
        runningBalance: runningTotal,
      });
    }
    return rows;
  }

  // We'll display descending in the Table (like your original),
  // so let's build ascending and then reverse:
  const rowsAsc = buildRowsAscending();
  const rowsDesc = [...rowsAsc].reverse();

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Ledger
      </Typography>

      {/* Filter section */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        {/* Account Filter */}
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="account-filter-label">Account</InputLabel>
          <Select
            labelId="account-filter-label"
            label="Account"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            <MenuItem value="">
              <em>-- All Accounts --</em>
            </MenuItem>
            {accounts.map((acc) => (
              <MenuItem key={acc._id} value={acc._id}>
                {acc.accountName} ({acc.accountNo})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Start Date */}
        <TextField
          label="Start Date"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />

        {/* End Date */}
        <TextField
          label="End Date"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />

        {/* Buttons */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" onClick={handleSearch}>
            Search
          </Button>
          {(accountId || startDate || endDate) && (
            <Button variant="outlined" onClick={handleClearFilter}>
              Clear
            </Button>
          )}
        </Box>
      </Box>

      {/* Table + Pagination */}
      {loading ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Running Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rowsDesc.length > 0 ? (
                rowsDesc.map((row) => {
                  if (row.isOpeningRow) {
                    // Opening-balance row
                    return (
                      <TableRow key={row._id}>
                        <TableCell colSpan={6} align="right" sx={{ fontWeight: "bold" }}>
                          {row.dateLabel}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {row.runningBalance.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    );
                  }

                  const dateStr = row.date.toLocaleDateString();
                  const netDisplay =
                    row.amount > 0
                      ? `+${row.amount.toFixed(2)}`
                      : row.amount < 0
                      ? row.amount.toFixed(2)
                      : "0.00";
                  const amountColor =
                    row.amount > 0 ? "green" : row.amount < 0 ? "red" : "inherit";
                  const balanceColor = row.runningBalance >= 0 ? "green" : "red";

                  return (
                    <TableRow key={row._id}>
                      <TableCell>{dateStr}</TableCell>
                      <TableCell>
                        {row.account?.accountName} ({row.account?.accountNo})
                      </TableCell>
                      <TableCell>{row.transactionType || "-"}</TableCell>
                      <TableCell>{row.categoryName || "-"}</TableCell>
                      <TableCell>{row.description || "N/A"}</TableCell>
                      <TableCell
                        sx={{ color: amountColor, cursor: "pointer" }}
                        onClick={() => handleRunningBalanceClick(row.account?._id)}
                        title="Click to filter by this account"
                      >
                        {netDisplay}
                      </TableCell>
                      <TableCell
                        sx={{ color: balanceColor, cursor: "pointer" }}
                        onClick={() => handleRunningBalanceClick(row.account?._id)}
                        title="Click to filter by this account"
                      >
                        {row.runningBalance.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No ledger entries found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>

            {/* TableFooter for pagination */}
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Rows per page"
                />
              </TableRow>
            </TableFooter>
          </Table>
        </Paper>
      )}
    </Box>
  );
}

export default LedgerPage;
